"use strict";
/*!
Copyright (C) 2020 Evolved Binary

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <https://www.gnu.org/licenses/>.
*/
Object.defineProperty(exports, "__esModule", { value: true });
exports.structurePlugin = exports.structurePluginKey = void 0;
exports.toggleStructurePluginCommand = toggleStructurePluginCommand;
const prosemirror_state_1 = require("prosemirror-state");
const utils_1 = require("./utils");
const suggestions_plugin_1 = require("./suggestions-plugin");
const lwdita_ast_1 = require("@evolvedbinary/lwdita-ast");
exports.structurePluginKey = new prosemirror_state_1.PluginKey('structureView');
exports.structurePlugin = new prosemirror_state_1.Plugin({
    key: exports.structurePluginKey,
    state: {
        init: () => ({ active: false }),
        apply(tr, value) {
            const meta = tr.getMeta(exports.structurePluginKey);
            if (meta === null || meta === void 0 ? void 0 : meta.toggle) {
                return Object.assign(Object.assign({}, value), { active: !value.active });
            }
            return value;
        },
    },
    view(_view) {
        return {
            update(view) {
                // Update the panel visibility based on the plugin state
                const { active } = exports.structurePluginKey.getState(view.state);
                document.body.classList.toggle('structure', active);
                if (!active) {
                    destroy();
                    return;
                }
                const $from = view.state.selection.$from;
                renderAttributesEditor($from, view);
            }
        };
    },
});
function toggleStructurePluginCommand() {
    return (state, dispatch) => {
        if (dispatch) {
            const tr = state.tr.setMeta(exports.structurePluginKey, { toggle: true });
            dispatch(tr);
        }
        return true;
    };
}
function getNodeAttributes(node) {
    const attributes = {};
    for (const [key, value] of Object.entries(node.attrs || {})) {
        attributes[key] = value.toString();
    }
    return attributes;
}
function renderAttributesEditor($from, view) {
    //if the panel is already open, remove it
    const existingPanel = document.getElementById('attributes-editor-panel');
    if (existingPanel) {
        existingPanel.remove();
    }
    const panel = document.createElement('div');
    panel.id = 'attributes-editor-panel';
    document.body.appendChild(panel);
    const node = $from.node();
    if (node) {
        // get attributes of the node from lwdita library
        const attrs = getNodeAttributes(node);
        panel.innerHTML = `<h1>\`${getNodeLabel(node)}\` attributes</h1>`;
        // for each attribute, create an input and label
        let path = (0, suggestions_plugin_1.pathToRoot)($from).reverse().map((nodeName) => (0, utils_1.schemaNodeNameToLwditaNodeName)(nodeName));
        path = path.map((name, idx) => {
            if (idx === path.length - 1) {
                return `<span class="breadcrumb" >${getNodePosRelativeToParent($from, name)}</span>`;
            }
            return `<span class="breadcrumb" >${name}</span>`;
        });
        panel.innerHTML += `<h1>Location:</h1>`;
        panel.innerHTML += `<h2>${path.join(' / ')}</h2>`;
        if (Object.keys(attrs).length > 0) {
            const ids = getNodesWithId(view); // Get all existing IDs in the document
            const attributesHtml = renderAttributes(attrs, ids);
            panel.innerHTML += `<div class="attributes">${attributesHtml}</div>`;
            panel.innerHTML += `<button id="save-attributes">Save Attributes</button>`;
            // Add event listener for the parent elements
            const parentElements = panel.querySelectorAll('h2 > span');
            parentElements.forEach((element, index) => {
                element.addEventListener('click', () => {
                    const distanceFromNode = parentElements.length - index - 2;
                    const ancestor = getAncestorAtDistance($from, distanceFromNode);
                    if (ancestor) {
                        const resolvedPos = view.state.doc.resolve(ancestor.pos);
                        renderAttributesEditor(resolvedPos, view);
                    }
                });
            });
            // Add event listener for the save button
            const saveButton = panel.querySelector('#save-attributes');
            if (saveButton) {
                saveButton.addEventListener('click', () => {
                    const updatedAttrs = {};
                    panel.querySelectorAll('.attribute input').forEach((input) => {
                        if (!input.id)
                            return; // Skip if no id
                        if (input.name === 'translate' && input.checked) {
                            updatedAttrs['translate'] = input.value;
                        }
                        else {
                            updatedAttrs[input.id] = input.value;
                        }
                    });
                    panel.querySelectorAll('.attribute select').forEach((select) => {
                        if (!select.id)
                            return; // Skip if no id
                        if (!select.value)
                            return; // Skip if no value
                        updatedAttrs[select.id] = `#${select.value}`;
                    });
                    const tr = view.state.tr.setNodeMarkup($from.before(), undefined, updatedAttrs);
                    view.dispatch(tr);
                });
            }
        }
        else {
            panel.innerHTML += `<p>No attributes found for this node.</p>`;
        }
    }
    else {
        panel.innerHTML = `<h1>No node at position ${$from.pos} </h1>`;
    }
}
function getNodeLabel(node) {
    const nodeName = (0, utils_1.schemaNodeNameToLwditaNodeName)(node.type.name);
    const NodeClass = (0, lwdita_ast_1.getNodeClass)(nodeName);
    const nodeInstance = new NodeClass(node.attrs);
    return nodeInstance.static.label || nodeName;
}
function destroy() {
    document.body.classList.remove('structure');
    const panel = document.getElementById('attributes-editor-panel');
    if (panel) {
        panel.remove();
    }
}
/**
 * Render the attributes as HTML inputs
 * @param attrs - All attributes of the node
 * @param ids - All existing IDs in the document (used for conref dropdown)
 * @returns html string
 */
function renderAttributes(attrs, ids) {
    let attributesHtml = ``;
    const sortedAttrs = Object.keys(attrs).sort((a, b) => getAttributeOptimalIndex(a) - getAttributeOptimalIndex(b));
    for (const attr of sortedAttrs) {
        switch (attr) {
            case 'class': // Skip the class attribute
            case 'parent': // Skip the parent attribute
                continue;
            case 'conref':
                attributesHtml += `<div class="attribute">
                  <label for="conref">conref:</label>
                    <select id="conref" name="conref">`;
                attributesHtml += `<option value="" ${!attrs[attr] ? 'selected' : ''}>-- none --</option>`;
                attributesHtml += ids.map((id) => {
                    return `<option value="${id}" ${attrs[attr] === `#${id}` ? 'selected' : ''}>${id}</option>`;
                }).join('\n');
                attributesHtml += `</select>
                </div>`;
                break;
            case 'href':
                attributesHtml += `<div class="attribute">
                  <label for="href">href:</label>
                    <select id="href" name="href">`;
                attributesHtml += `<option value="" ${!attrs[attr] ? 'selected' : ''}>-- none --</option>`;
                attributesHtml += ids.map((id) => {
                    return `<option value="${id}" ${attrs[attr] === `#${id}` ? 'selected' : ''}>${id}</option>`;
                }).join('\n');
                attributesHtml += `</select>
                </div>`;
                break;
            case 'translate':
                attributesHtml += `<div class="attribute">
  <label>translate:</label>
  <div class="radio-group">
    <input type="radio" name="translate" value="yes" id="translate-yes" ${attrs[attr] === 'yes' ? 'checked' : ''}>
    <label for="translate-yes">Yes</label>

    <input type="radio" name="translate" value="no" id="translate-no" ${attrs[attr] === 'no' ? 'checked' : ''}>
    <label for="translate-no">No</label>
  </div>
</div>`;
                break;
            default:
                attributesHtml += `<div class="attribute">
                  <label for="${attr}">${attr}:</label>
                  <input type="text" id="${attr}" value="${attrs[attr]}" />
                </div>`;
                break;
        }
    }
    return attributesHtml;
}
function getAttributeOptimalIndex(attr) {
    // Define the optimal order of attributes
    // This order is based on the common usage and importance of attributes in lwdita
    const attributeOptimalOrder = [
        `id`, `conref`, //%reuse;
        `keyref`, // keyref
        `href`, // href
        `type`, // note
        `colspan`, `rowspan`, `headers`, // table;
        `autoplay`, `controls`, `loop`, `muted`, `tabindex`, // media;
        `dir`, `xml:lang`, `translate`, //%localization;
        `format`, `scope`, //%reference-content;
        `props`, //%filters;
        `outputclass`
    ];
    return attributeOptimalOrder.indexOf(attr);
}
/**
 * Get all sibling nodes of the parent node ignoring text nodes
 * @param $from - resolved position
 * @returns all siblings of the parent node ignoring text nodes
 */
function getAllSiblings($from) {
    // Get the parent node
    const depth = $from.depth - 1 >= 0 ? $from.depth - 1 : 0;
    const parent = $from.node(depth);
    // Find the index of the current block node within that parent
    const index = $from.index(depth);
    const siblings = [];
    parent.forEach((node) => siblings.push(node));
    return {
        allSiblings: siblings,
        index,
    };
}
function getNodePosRelativeToParent($from, name) {
    const siblings = getAllSiblings($from);
    const { allSiblings, index } = siblings;
    let count = 0;
    for (let i = 0; i < index; i++) {
        if ((0, utils_1.schemaNodeNameToLwditaNodeName)(allSiblings[i].type.name) === name) {
            count++;
        }
    }
    if (count > 0) {
        return `${name}[${count + 1}]`;
    }
    return `${name}`;
}
function getAncestorAtDistance($pos, distance) {
    const depth = $pos.depth - distance;
    if (depth < 0)
        return null;
    const node = $pos.node(depth);
    const pos = depth > 0 ? $pos.before(depth) : 0;
    return { node, pos, depth };
}
function getNodesWithId(view) {
    const ids = [];
    view.state.doc.descendants((node) => {
        if (node.attrs && node.attrs.id) {
            ids.push(node.attrs.id);
        }
    });
    return ids;
}
//# sourceMappingURL=structure-plugin.js.map